import { sectionRenderer, morphSection, buildSectionSelector } from '@theme/section-renderer';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { CollectionUpdateEvent, SearchUpdateEvent } from '@shopify/events';

/**
 * Apply Search & Discovery facets to the native collection, retaining locale,
 * preview and app query parameters. A new selection always starts at page one.
 */
export function collectionURL(current, base, values, replaceFacets = false) {
  const url = new URL(current);
  if (replaceFacets) {
    url.pathname = new URL(base, url).pathname.replace(/\/$/, '');
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.')) url.searchParams.delete(key);
    }
  }
  // Native GET sort forms include active facets; replace each provided key once.
  for (const key of new Set([...values.keys()].filter((key) => key.startsWith('filter.')))) {
    url.searchParams.delete(key);
  }
  for (const [key, value] of values) {
    if (key === 'sort_by' || key === 'q' || key === 'type') url.searchParams.set(key, String(value));
    else if (key.startsWith('filter.') && String(value).trim()) url.searchParams.append(key, String(value));
  }
  url.searchParams.delete('page');
  url.searchParams.delete('section_id');
  url.hash = '';
  return url;
}

class CollectionControls extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-open-filters]')) this.openFilters();
      if (event.target.closest('[data-close-filters]')) this.closeFilters();
      const clear = event.target.closest('[data-clear-filters]');
      if (clear) {
        event.preventDefault();
        if (this.hasAttribute('aria-busy')) return;
        const url = collectionURL(location.href, clear.href, new URLSearchParams(), true);
        this.update(url, { filtering: true });
      }
    }, { signal });
    this.addEventListener('submit', (event) => {
      if (!(event.target instanceof HTMLFormElement)) return;
      const form = event.target;
      if (!form.matches('[data-filter-form], [data-sort-form]')) return;
      event.preventDefault();
      const filtering = form.matches('[data-filter-form]');
      const base = form.dataset.baseUrl || form.action;
      const url = collectionURL(location.href, base, new FormData(form), filtering);
      this.update(url, { filtering });
    }, { signal });
    this.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement) || !event.target.closest('[data-sort-form]')) return;
      event.target.form?.requestSubmit();
    }, { signal });
    this.addEventListener('close', () => unlockScroll(this), { capture: true, signal });
    const dialog = this.querySelector('dialog');
    // Native dialog cancel does not bubble. Bind directly for Escape dismissal.
    dialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeFilters();
    }, { signal });
    dialog?.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !dialog.open) return;
      const focusable = [...dialog.querySelectorAll('button:enabled, input:enabled, select:enabled, textarea:enabled, a[href], [tabindex="0"]')]
        .filter((element) => element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }, { signal });
    dialog?.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeFilters();
    }, { signal });
    document.addEventListener('click', (event) => {
      if (event.target instanceof Node && !this.contains(event.target)) this.querySelector('.collection-sort')?.removeAttribute('open');
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.querySelector('dialog')?.open && this.querySelector('.collection-sort[open]')) {
        this.querySelector('.collection-sort')?.removeAttribute('open');
        this.querySelector('.collection-sort summary')?.focus();
      }
    }, { signal });
    window.addEventListener('popstate', () => this.update(new URL(location.href), { push: false }), { signal });
    this.initGrid();
    this.initDragging(signal);
    this.reveal();
    // Native pagination appends new placements without reloading this element.
    this.observeGrid();
    // A clean Search document can include non-product matches. Request the
    // native product-only section without redirecting or adding browser history.
    if (this.dataset.searchTypes && this.dataset.searchTypes !== 'product') {
      const count = this.querySelector('.theme-search-results__count');
      if (count) count.hidden = true;
      this.update(new URL(location.href), { push: false, focus: false });
    }
  }

  observeGrid() {
    this.gridObserver?.disconnect();
    this.gridObserver = new MutationObserver(() => this.reveal());
    const grid = this.closest('results-list')?.querySelector('[ref="grid"]');
    if (grid) this.gridObserver.observe(grid, { childList: true });
  }

  openFilters() {
    const dialog = this.querySelector('dialog');
    if (!dialog || dialog.open) return;
    // A dismissed draft never becomes the next drawer's applied selection.
    this.querySelector('[data-filter-form]')?.reset();
    this.querySelector('[data-filter-error]').hidden = true;
    this.returnFocus = document.activeElement;
    dialog.showModal();
    lockScroll(this);
  }

  closeFilters() {
    this.request?.abort();
    this.querySelector('dialog')?.close();
    unlockScroll(this);
    this.returnFocus?.focus();
  }

  async update(url, { filtering = false, push = true, focus = true } = {}) {
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    const sectionId = this.dataset.sectionId;
    const dialogWasOpen = this.querySelector('dialog')?.open;
    const oldURL = location.href;
    const isSearch = this.dataset.searchQuery !== undefined;
    if (isSearch) url.searchParams.set('type', 'product');
    this.setAttribute('aria-busy', 'true');
    this.querySelectorAll('button[type="submit"], [data-sort-form] input').forEach((input) => { input.disabled = true; });
    this.querySelector('[data-filter-error]').hidden = true;
    const status = document.getElementById('CollectionStatus-' + sectionId);
    try {
      // The renderer caches raw HTML before validation; fetch fresh so failed responses remain retryable.
      // Validate before touching either history or the rendered products.
      const renderURL = new URL(url);
      const shopify = window.Shopify;
      const inPreview = shopify?.previewMode && shopify.theme?.role === 'unpublished'
        && /^\d+$/.test(String(shopify.theme.id));
      const previewThemeId = inPreview
        ? renderURL.searchParams.get('preview_theme_id') || String(shopify.theme.id)
        : null;
      // Shopify's preview redirect collapses repeated facet parameters. Use
      // the preview session for rendering and verify its theme-specific asset.
      if (previewThemeId) renderURL.searchParams.delete('preview_theme_id');
      const moduleURL = new URL(import.meta.url);
      const parseSection = (html) => new DOMParser().parseFromString(html, 'text/html')
        .getElementById(buildSectionSelector(sectionId));
      const matchesTheme = (section) => {
        const asset = section?.querySelector('script[src*="/assets/collection-page.js"]')?.getAttribute('src');
        if (!asset) return false;
        const assetURL = new URL(asset, location.href);
        return assetURL.origin === moduleURL.origin && assetURL.pathname === moduleURL.pathname;
      };
      let html = await sectionRenderer.getSectionHTML(sectionId, false, new URL(renderURL), request.signal);
      if (request.signal.aborted) return;
      let next = parseSection(html);
      if (previewThemeId && !matchesTheme(next)) {
        // Another preview can change the shared cookie. Repin once, discard
        // that redirect's HTML, then request the complete facet query again.
        const pinURL = new URL(renderURL);
        pinURL.searchParams.set('preview_theme_id', previewThemeId);
        await sectionRenderer.getSectionHTML(sectionId, false, pinURL, request.signal);
        if (request.signal.aborted) return;
        html = await sectionRenderer.getSectionHTML(sectionId, false, new URL(renderURL), request.signal);
        if (request.signal.aborted) return;
        next = parseSection(html);
      }
      const nextControls = next?.querySelector('collection-controls');
      if (!nextControls || (isSearch && nextControls.dataset.searchTypes !== 'product') || (previewThemeId && !matchesTheme(next))) {
        throw new Error('Invalid collection section response');
      }
      const displayURL = new URL(url);
      if (isSearch) displayURL.searchParams.delete('type');
      if (push) history.pushState({ collection: sectionId }, '', displayURL);
      const EventType = isSearch ? SearchUpdateEvent : CollectionUpdateEvent;
      const deferred = EventType.createPromise();
      // Prevent an unhandled rejection when no analytics subscriber is installed.
      deferred.promise.catch(() => {});
      const payload = isSearch ? {
        search: { query: this.dataset.searchQuery },
        promise: deferred.promise,
      } : {
        collection: {
          id: /^\d+$/.test(this.dataset.collectionId) ? this.dataset.collectionId : null,
          handle: this.dataset.collectionHandle,
          productsCount: Number(this.dataset.resultsCount) || 0,
        },
        productFilters: CollectionUpdateEvent.parseProductFilters(),
        sortKey: CollectionUpdateEvent.getSortKey(),
        promise: deferred.promise,
      };
      this.dispatchEvent(new EventType(payload));
      try {
        // Close the modal before morphing so a preserved dialog cannot strand
        // its top-layer state, scroll lock or focus across the section update.
        this.querySelector('dialog')?.close();
        unlockScroll(this);
        await morphSection(sectionId, html);
        const section = document.getElementById(buildSectionSelector(sectionId));
        const controls = section.querySelector('collection-controls');
        controls.initGrid();
        controls.reveal();
        controls.observeGrid();
        if (isSearch) section.querySelector('results-list')?.startPagination?.();
        const count = Number(controls.dataset.resultsCount) || 0;
        deferred.resolve(isSearch ? { totalCount: count } : { productsCount: count });
        const freshStatus = section.querySelector('[data-collection-status]');
        freshStatus.textContent = controls.dataset.resultsMessage.replace('[count]', String(count));
        if (focus) {
          if (filtering || dialogWasOpen) controls.querySelector('[data-open-filters]')?.focus();
          else controls.querySelector('.collection-sort summary')?.focus();
        }
      } catch (error) {
        deferred.reject(error);
        if (push) history.replaceState(null, '', oldURL);
        throw error;
      }
    } catch (error) {
      if (request.signal.aborted) return;
      if (filtering) {
        this.querySelector('[data-filter-error]').hidden = false;
        const dialog = this.querySelector('dialog');
        if (dialogWasOpen && dialog && !dialog.open && this.isConnected) {
          dialog.showModal();
          lockScroll(this);
        }
      }
      else if (status) {
        status.textContent = this.dataset.errorMessage;
        if (!focus) status.classList.remove('visually-hidden');
      }
      this.querySelector('[data-sort-form]')?.reset();
    } finally {
      if (this.request === request) {
        this.removeAttribute('aria-busy');
        this.querySelectorAll('button[type="submit"], [data-sort-form] input').forEach((input) => { input.disabled = false; });
      }
    }
  }

  initGrid() {
    const results = this.closest('results-list');
    // This PLP uses merchant-selected column counts, not Horizon's saved zoom.
    results?.querySelector('[ref="grid"]')?.setAttribute('product-grid-view', 'default');
    // Keep native empty-result recovery on this template and preserve app context.
    const clearLink = results?.querySelector('.main-collection-grid__empty-link');
    if (clearLink) clearLink.href = collectionURL(location.href, clearLink.href, new URLSearchParams(), true).href;
  }

  initDragging(signal) {
    const nav = this.querySelector('.collection-subnav');
    if (!nav) return;
    let drag;
    let dragged = false;
    nav.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      dragged = false;
      drag = { x: event.clientX, scroll: nav.scrollLeft, id: event.pointerId };
    }, { signal });
    nav.addEventListener('pointermove', (event) => {
      if (!drag) return;
      if (event.buttons !== 1) { drag = null; return; }
      const distance = event.clientX - drag.x;
      if (Math.abs(distance) > 6) {
        dragged = true;
        nav.setPointerCapture(drag.id);
        nav.scrollLeft = drag.scroll - distance;
      }
    }, { signal });
    const finish = () => { drag = null; };
    nav.addEventListener('pointerup', finish, { signal });
    nav.addEventListener('pointercancel', finish, { signal });
    nav.addEventListener('lostpointercapture', finish, { signal });
    nav.addEventListener('click', (event) => {
      if (dragged) { event.preventDefault(); dragged = false; }
    }, { signal });
    nav.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
  }

  reveal() {
    this.motion?.disconnect();
    const root = this.closest('results-list');
    if (!root || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.motion = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        this.motion.unobserve(entry.target);
      }
    }, { threshold: .12 });
    root.querySelectorAll('.theme-motion:not(.is-visible)').forEach((node) => {
      node.classList.add('theme-motion--ready');
      this.motion.observe(node);
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.request?.abort();
    this.motion?.disconnect();
    this.gridObserver?.disconnect();
    unlockScroll(this);
  }
}

if (!customElements.get('collection-controls')) customElements.define('collection-controls', CollectionControls);
